import { cn } from '@/lib/utils';
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import healthcomLogo from '../assets/healthcom-logo.png';
import { SELECTED_CLINIC_ID_STORAGE_KEY } from '../constants/brand';
import { PatientClinic } from '../contexts/PatientPortalContext';
import { fetchMeClinicsCached } from '../hooks/useMeClinics';
import { AuthMatch, authService, isNewPasswordRequired } from '../services/auth.service';
import { portalSession } from '../services/portalSession';
import Container from './Container';
import FallbackImage from './common/FallbackImage';
import { useLoadingOverlay } from './common/LoadingOverlay';

// Local stand-in for the AuthUser shape this component used while it
// was Cognito-backed. Custom auth replaces Cognito; nothing in the UI
// reads beyond "are we authed", so a thin object with `userId` is
// enough.
type AuthUser = { userId: string };

type AuthStep =
  | 'login'
  | 'code'
  | 'clinics'
  | 'loading'
  | 'authed'
  | 'legacy'
  | 'legacy-new-password';
type AuthMode = 'email' | 'phone';

const COMMON_COUNTRIES: CountryCode[] = ['US', 'CA', 'MX', 'GB', 'AU'];

// Render the flag emoji for an ISO-3166 alpha-2 country code by mapping each
// letter to its Regional Indicator Symbol. Works on iOS/macOS natively;
// Android falls back to the two-letter code, which is still readable.
const flagFor = (iso: string): string =>
  iso
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(0x1f1a5 + c.charCodeAt(0)))
    .join('');

// Countries sorted with the most likely ones first (US, CA, MX, GB, AU),
// then alphabetical by ISO code. Built once per module load.
const COUNTRY_OPTIONS = (() => {
  const all = getCountries();
  const common = COMMON_COUNTRIES.filter(c => all.includes(c));
  const rest = all.filter(c => !COMMON_COUNTRIES.includes(c)).sort();

  return [...common, ...rest].map(iso => ({
    iso,
    callingCode: getCountryCallingCode(iso),
    label: `${flagFor(iso)} +${getCountryCallingCode(iso)}`,
  }));
})();

// Shared shell for every step so the login / code / clinics screens feel
// like one flow — same gradient, same icon chip + heading treatment, same
// white card with a soft ring.
type HeroShellProps = {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
};

const HeroShell: React.FC<HeroShellProps> = ({ icon, title, subtitle, footer, children }) => (
  <div className="min-h-dvh flex flex-col items-center justify-center px-4 antialiased bg-linear-to-br from-primary to-primary/60 dark:from-zinc-900 dark:to-zinc-900 dark:bg-none">
    <div className="w-full max-w-sm">
      <div className="text-center text-white mb-6">
        <div className="mx-auto size-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20 dark:bg-white/5 dark:ring-white/10 mb-4">
          {icon}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-white/80 dark:text-zinc-400">{subtitle}</p>}
      </div>

      <div className="rounded-3xl bg-white dark:bg-zinc-800 p-6 shadow-xl dark:shadow-none ring-1 ring-black/5 dark:ring-white/10">
        {children}
      </div>

      {footer && (
        <div className="mt-6 text-center text-xs text-white/70 dark:text-zinc-500">{footer}</div>
      )}
    </div>
  </div>
);

// Matches the input style already in use on the login step — border-0 to
// defeat @tailwindcss/forms' default gray border, soft gray ring, primary
// focus ring.
const heroInputClass =
  'w-full rounded-xl border-0 bg-white dark:bg-zinc-900 px-3.5 py-2.5 text-sm text-gray-900 dark:text-zinc-100 ring-1 ring-gray-200 dark:ring-white/10 outline-2 -outline-offset-1 outline-transparent focus:outline-primary placeholder:text-gray-400 dark:placeholder:text-zinc-500 max-sm:text-base/5';

// Envelope-with-check icon for the verification step.
const CodeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-7 text-white"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 6h16v12H4z" />
    <path d="M4 6l8 7 8-7" />
  </svg>
);

// Building icon for the clinic picker step.
const ClinicIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-7 text-white"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 21h18" />
    <path d="M5 21V7l7-4 7 4v14" />
    <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />
  </svg>
);

const RootLanding = () => {
  const queryClient = useQueryClient();
  const [authStep, setAuthStep] = useState<AuthStep>('loading');
  const [, setUser] = useState<AuthUser | null>(null);
  const [mode, setMode] = useState<AuthMode>('email');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState<CountryCode>('US');
  const [nationalNumber, setNationalNumber] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [clinics, setClinics] = useState<PatientClinic[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Custom-auth bridge state. The challengeId is returned by
  // `authService.startOtp` and consumed by `verifyOtp` /
  // `startSession`. `matches` carries the patient/clinic candidates
  // surfaced by the verify step so the clinic picker can issue a
  // patient-scoped session for whichever clinic the user picks.
  const [challengeId, setChallengeId] = useState('');
  // Set when the backend tells us the start was throttled (no SMS /
  // email actually dispatched). Drives the "please wait Xs" message
  // on the code-entry screen so the user knows nothing new is coming.
  const [throttleWaitSeconds, setThrottleWaitSeconds] = useState(0);
  const [matches, setMatches] = useState<AuthMatch[]>([]);
  // Legacy username/password fallback. Distinct from `email`/`code`
  // state so toggling between flows doesn't carry partial input over.
  const [legacyUsername, setLegacyUsername] = useState('');
  const [legacyPassword, setLegacyPassword] = useState('');
  // NEW_PASSWORD_REQUIRED follow-up — first leg of `/auth/legacy/login`
  // returns the Cognito Session token, which we hand back to the
  // `/auth/legacy/login/new-password` route along with the new
  // password the patient picks here.
  const [legacyChallengeSession, setLegacyChallengeSession] = useState('');
  const [legacyNewPasswordValue, setLegacyNewPasswordValue] = useState('');
  const [legacyNewPasswordConfirm, setLegacyNewPasswordConfirm] = useState('');

  // Register with the app-level loading overlay while we're in the
  // pre-auth check. The overlay (mounted in App.tsx) renders the
  // HealthcomLoadingPage on top of whatever this component returns —
  // we just signal "I'm loading". Container also registers when it
  // mounts (post-auth), and the overlay stays visible as long as
  // either consumer is loading; that's how we get a seamless
  // RootLanding → Container handoff with no animation restart.
  useLoadingOverlay(authStep === 'loading');

  const handleCountryChange = (iso: CountryCode) => {
    setCountry(iso);
    const digits = nationalNumber.replace(/\D/g, '');

    if (digits) {
      setNationalNumber(new AsYouType(iso).input(digits));
    }
  };

  const handleNationalNumberChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setNationalNumber(new AsYouType(country).input(digits));
  };

  const cognitoUsername = useMemo(() => {
    if (mode === 'email') {
      const v = email.trim().toLowerCase();

      return v && v.includes('@') ? v : null;
    }

    const parsed = parsePhoneNumberFromString(nationalNumber, country);

    return parsed && parsed.isValid() ? parsed.number : null;
  }, [mode, email, nationalNumber, country]);

  const canSubmit = cognitoUsername !== null && !isSubmitting;

  // Fetches the clinics for the authenticated user. Throws on failure so the
  // two callers (session-restore on mount vs. explicit login after OTP) can
  // handle errors differently — the restore path silently signs out, the
  // login path surfaces the error to the user.
  //
  // Uses the shared React Query cache so Container's Switch Clinic flow
  // reads the same data without re-fetching.
  const discoverClinics = useCallback(async (): Promise<void> => {
    setAuthStep('loading');

    const data = await fetchMeClinicsCached(queryClient);
    // Log raw API response so we can verify the pipeline in DevTools.
    console.log(
      '[colors:RootLanding:meClinics:raw]',
      (data?.clinics || []).map((c: PatientClinic) => ({
        clinicId: c.clinicId,
        clinicName: c.clinicName,
        primaryColor: c.primaryColor,
        accentColor: c.accentColor,
      }))
    );
    // Use the clinic colors as returned by the API. The picker's initial-
    // letter chip fallbacks already fall back to HealthCom green when a
    // clinic's `primaryColor` is empty (see portal/ClinicSelect.tsx).
    const clinicList: PatientClinic[] = data?.clinics || [];

    if (clinicList.length === 0) {
      throw new Error('No clinics found for your account.');
    }

    // Single clinic: auto-select, persist the clinicId, hand off to
    // Container via in-place render (no URL change).
    if (clinicList.length === 1) {
      try {
        localStorage.setItem(SELECTED_CLINIC_ID_STORAGE_KEY, String(clinicList[0].clinicId));
      } catch {
        /* storage blocked — Container will fall back to URL path */
      }

      setAuthStep('authed');

      return;
    }

    // Multi-clinic: if the user already picked one previously, skip the
    // picker and hand off directly. Container reads the same localStorage
    // key to pick up where they left off. Only show the picker when
    // there's no saved choice or the saved clinic is no longer in the
    // user's list (access revoked, etc.).
    try {
      const savedRaw = localStorage.getItem(SELECTED_CLINIC_ID_STORAGE_KEY);
      const savedId = savedRaw ? Number(savedRaw) : NaN;
      const hasSavedClinic =
        Number.isFinite(savedId) && clinicList.some(c => c.clinicId === savedId);

      if (hasSavedClinic) {
        setAuthStep('authed');

        return;
      }
    } catch {
      /* storage blocked — fall through to picker */
    }

    setClinics(clinicList);
    setAuthStep('clinics');
  }, [queryClient]);

  useEffect(() => {
    const checkAuth = async () => {
      // Custom auth: the session lives in localStorage. If we have a
      // live token, prove it works by hitting `/me/clinics` (via
      // `discoverClinics`). On any error we drop to the login screen
      // silently so a stale token doesn't strand the user on a dead
      // loading screen.
      if (portalSession.isLikelyValid()) {
        try {
          const stored = portalSession.getPatient();

          if (stored) setUser({ userId: stored.patientId });
          await discoverClinics();

          return;
        } catch (err) {
          console.warn('Session restore failed, signing out silently:', err);
          await authService.logout().catch(() => {});
          setUser(null);
        }
      }

      setAuthStep('login');
    };

    checkAuth();
  }, [discoverClinics]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!cognitoUsername) {
      setError(
        mode === 'phone'
          ? 'Please enter a valid phone number for the selected country.'
          : 'Please enter a valid email address.'
      );

      return;
    }

    setIsSubmitting(true);

    try {
      const isEmail = mode === 'email';
      const res = await authService.startOtp({
        contact: cognitoUsername,
        contactType: isEmail ? 'email' : 'phone',
      });

      setChallengeId(res.challengeId);
      setThrottleWaitSeconds(res.throttled ? (res.retryAfterSeconds ?? 0) : 0);
      setAuthStep('code');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err?.message || 'Login failed. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const verifyRes = await authService.verifyOtp({ challengeId, code });

      if (!verifyRes.matches?.length) {
        setError('No accounts found for that contact.');
        setIsSubmitting(false);

        return;
      }

      // Issue a session for the first match so subsequent calls
      // (`/me/clinics` inside `discoverClinics`) authenticate. If the
      // user has patients across multiple clinics the picker will
      // surface them; on selection we re-issue against the chosen
      // clinic's match (see `handleSelectClinic`).
      const first = verifyRes.matches[0];

      await authService.startSession({
        challengeId,
        patientId: first.patientId,
        clinicId: first.clinicId,
      });
      setMatches(verifyRes.matches);
      setUser({ userId: first.patientId });
      await discoverClinics();
    } catch (err: any) {
      console.error('Code verification error:', err);
      setError(err?.message || 'Invalid code. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleSelectClinic = async (clinic: PatientClinic) => {
    // Persist the picked clinicId for Container.
    try {
      localStorage.setItem(SELECTED_CLINIC_ID_STORAGE_KEY, String(clinic.clinicId));
    } catch {
      /* storage blocked — Container will fall back to URL path */
    }

    // Sessions are still patient-bound on the backend, but the rebind
    // is done via /me/switch-clinic against the authed session — no
    // OTP challenge involved, no new token. The server confirms the
    // target clinic hosts a Patient row that shares the session's
    // verified email/phone before flipping the binding.
    const current = portalSession.getPatient();

    if (current && current.clinicId !== clinic.clinicId) {
      try {
        setAuthStep('loading');
        await authService.switchClinic({ clinicId: clinic.clinicId });
      } catch (err: any) {
        // Avoid logging the full error payload — this is a patient
        // portal and the normalized API error can carry response
        // bodies. Status + message is enough for a console breadcrumb.
        console.error('Failed to switch clinic:', err?.response?.status, err?.message);
        setError(err?.message || 'Could not switch to that clinic.');
        setAuthStep('clinics');

        return;
      }
    }

    setAuthStep('authed');
  };

  // Legacy username/password login. The username keys a specific
  // patient row in the legacy Cognito pool, so the session comes back
  // already bound to one patient + clinic. Skip the clinic picker —
  // we know exactly who they are.
  const handleLegacyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!legacyUsername || !legacyPassword) {
      setError('Please enter your username and password.');

      return;
    }

    setIsSubmitting(true);

    try {
      const res = await authService.legacyLogin({
        username: legacyUsername.trim(),
        password: legacyPassword,
      });

      // Cognito needs the patient to set a permanent password (their
      // account was admin-created in the legacy pool). Stash the
      // session and route to the new-password step; password is
      // cleared so it can't be re-submitted accidentally.
      if (isNewPasswordRequired(res)) {
        setLegacyChallengeSession(res.session);
        setLegacyPassword('');
        setLegacyNewPasswordValue('');
        setLegacyNewPasswordConfirm('');
        setAuthStep('legacy-new-password');
        // The early-return below skips the trailing
        // `setIsSubmitting(false)`, so clear it explicitly here.
        // Without this the new-password screen renders with the
        // submit button locked on "Setting password…".
        setIsSubmitting(false);

        return;
      }

      try {
        localStorage.setItem(SELECTED_CLINIC_ID_STORAGE_KEY, String(res.patient.clinicId));
      } catch {
        /* storage blocked — Container will fall back to URL path */
      }

      setUser({ userId: res.patient.patientId });
      setAuthStep('authed');
    } catch (err: any) {
      console.error('Legacy login error:', err);
      setError(err?.message || 'Sign in failed. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleLegacyNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!legacyNewPasswordValue || !legacyNewPasswordConfirm) {
      setError('Please enter and confirm your new password.');

      return;
    }

    if (legacyNewPasswordValue !== legacyNewPasswordConfirm) {
      setError('Passwords don’t match.');

      return;
    }

    setIsSubmitting(true);

    try {
      const res = await authService.legacyNewPassword({
        username: legacyUsername.trim(),
        newPassword: legacyNewPasswordValue,
        session: legacyChallengeSession,
      });

      try {
        localStorage.setItem(SELECTED_CLINIC_ID_STORAGE_KEY, String(res.patient.clinicId));
      } catch {
        /* storage blocked */
      }

      setUser({ userId: res.patient.patientId });
      setAuthStep('authed');
    } catch (err: any) {
      console.error('Legacy new-password error:', err);
      const status = err?.response?.data?.statusCode || err?.statusCode;
      const message = err?.response?.data?.message || err?.message;

      if (status === 401) {
        // Session expired or wasn't accepted — bounce back to the
        // username/password screen and have them sign in again.
        setError(message || 'Your session expired. Please sign in again.');
        setLegacyChallengeSession('');
        setLegacyNewPasswordValue('');
        setLegacyNewPasswordConfirm('');
        setAuthStep('legacy');
      } else {
        setError(message || 'Could not set your new password. Try again.');
      }
    }

    setIsSubmitting(false);
  };

  // Reset RootLanding's auth state machine back to the login screen.
  // Container handles its own signOut + cleanup; this only resets the
  // state RootLanding owns so it stops rendering Container.
  const resetAuthState = useCallback(() => {
    setUser(null);
    setClinics([]);
    setEmail('');
    setNationalNumber('');
    setCode('');
    setError('');
    setChallengeId('');
    setMatches([]);
    setLegacyUsername('');
    setLegacyPassword('');
    setLegacyChallengeSession('');
    setLegacyNewPasswordValue('');
    setLegacyNewPasswordConfirm('');
    setAuthStep('login');
  }, []);

  const handleLogout = async () => {
    await authService.logout();
    resetAuthState();
  };

  // Loader visibility is handled by the app-level LoadingOverlay
  // provider — see `useLoadingOverlay(authStep === 'loading')`
  // above. While we're loading, the overlay covers the viewport
  // and we render nothing here. The instant we transition out of
  // 'loading', the overlay's settle/fade-out kicks in and reveals
  // whichever step branch below.
  if (authStep === 'loading') {
    return null;
  }

  // Authed + clinic selected — hand off to the portal. Renders inline so
  // the URL stays put. Container reads the active clinicId from
  // localStorage, so no path param is needed.
  if (authStep === 'authed') {
    return <Container onSignedOut={resetAuthState} />;
  }

  // ---------- Verification code step ----------
  if (authStep === 'code') {
    const destinationHint = mode === 'phone' ? 'your phone' : 'your email';
    const subtitle =
      throttleWaitSeconds > 0
        ? `We didn't send another code — please wait about ${throttleWaitSeconds} seconds before requesting a new one. If you already received a code, you can enter it below.`
        : `We sent a verification code to ${destinationHint}.`;

    return (
      <HeroShell
        icon={<CodeIcon />}
        title="Enter your code"
        subtitle={subtitle}
        footer={<>Didn't get it? Check your spam folder or try again.</>}
      >
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-500/10 p-3 ring-1 ring-red-100 dark:ring-red-500/20">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        <form onSubmit={handleVerifyCode} className="flex flex-col gap-4">
          <div>
            <label
              htmlFor="code"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Verification code
            </label>
            {/* Single rounded readout matching the public-request
                code-entry input: each digit position renders as a
                fixed `1ch` slot showing either the typed digit or a
                faint bullet, and a full-width hidden input behind
                catches every keystroke (with
                `autocomplete="one-time-code"` so iOS can fill the
                SMS code in one tap). The caret is hidden so the
                display stays clean. Cognito challenge codes are
                6 digits. */}
            <div className="relative mt-1.5 rounded-2xl bg-gray-50 dark:bg-white/5 ring-2 ring-gray-950/10 dark:ring-white/10 px-5 py-5 sm:py-6 focus-within:ring-primary focus-within:bg-white dark:focus-within:bg-white/10 transition-shadow">
              <div
                className="text-center text-3xl sm:text-4xl font-bold tabular-nums select-none"
                aria-hidden="true"
              >
                {Array.from({ length: 6 }).map((_, i) => {
                  const ch = code[i];

                  return (
                    <span
                      key={i}
                      className={`inline-block w-[1ch] text-center ${
                        ch ? 'text-gray-900 dark:text-zinc-100' : 'text-gray-300 dark:text-zinc-600'
                      } ${i > 0 ? 'ml-3 sm:ml-4' : ''}`}
                    >
                      {ch ?? '•'}
                    </span>
                  );
                })}
              </div>
              <input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                aria-label="Verification code"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                className="absolute inset-0 size-full opacity-0 cursor-text [caret-color:transparent]"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !code}
            className={cn(
              'w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white ring-1',
              isSubmitting || !code
                ? 'bg-gray-300 ring-gray-300 cursor-not-allowed'
                : 'bg-primary ring-primary hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
            )}
          >
            {isSubmitting ? 'Verifying…' : 'Verify'}
          </button>

          <button
            type="button"
            onClick={() => {
              setCode('');
              setError('');
              setAuthStep('login');
            }}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            Back to sign in
          </button>
        </form>
      </HeroShell>
    );
  }

  // ---------- Clinic picker step ----------
  if (authStep === 'clinics') {
    return (
      <HeroShell
        icon={<ClinicIcon />}
        title="Choose your clinic"
        subtitle="You have accounts at multiple clinics."
        footer={
          <button type="button" onClick={handleLogout} className="text-white/70 hover:text-white">
            Sign out
          </button>
        }
      >
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-500/10 p-3 ring-1 ring-red-100 dark:ring-red-500/20">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {clinics.map(clinic => (
            <button
              key={clinic.clinicId}
              type="button"
              onClick={() => handleSelectClinic(clinic)}
              className="flex items-center gap-3 rounded-xl bg-white dark:bg-zinc-900 p-3.5 ring-1 ring-gray-200 dark:ring-white/10 hover:ring-primary/50 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-left"
            >
              <FallbackImage
                src={clinic.logo}
                alt={clinic.clinicName}
                imgClassName="size-10 rounded-xl object-contain"
                fallback={
                  <span
                    className="inline-flex size-10 items-center justify-center rounded-xl text-sm font-bold text-white"
                    style={{ backgroundColor: clinic.primaryColor || 'var(--primary-color)' }}
                  >
                    {clinic.clinicName?.charAt(0)?.toUpperCase() || 'C'}
                  </span>
                }
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100 truncate">
                  {clinic.clinicName}
                </p>
                {clinic.city && clinic.state && (
                  <p className="text-xs text-gray-400 dark:text-zinc-500">
                    {clinic.city}, {clinic.state}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      </HeroShell>
    );
  }

  // ---------- Legacy username/password step ----------
  if (authStep === 'legacy') {
    return (
      <HeroShell
        icon={
          <img
            src={healthcomLogo}
            alt="HealthCom"
            className="size-14 rounded-2xl bg-white object-contain p-2"
          />
        }
        title="HealthCom Patient Portal"
        subtitle="Sign in with your username and password."
        footer={<>Trouble signing in? Contact your clinic.</>}
      >
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-500/10 p-3 ring-1 ring-red-100 dark:ring-red-500/20">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        <form onSubmit={handleLegacyLogin} className="flex flex-col gap-4">
          <div>
            <label
              htmlFor="legacy-username"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Username
            </label>
            <input
              id="legacy-username"
              name="username"
              type="text"
              autoComplete="username"
              value={legacyUsername}
              onChange={e => setLegacyUsername(e.target.value)}
              className={cn(heroInputClass, 'mt-1.5')}
              required
              autoFocus
            />
          </div>
          <div>
            <label
              htmlFor="legacy-password"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Password
            </label>
            <input
              id="legacy-password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={legacyPassword}
              onChange={e => setLegacyPassword(e.target.value)}
              className={cn(heroInputClass, 'mt-1.5')}
              required
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !legacyUsername || !legacyPassword}
            className={cn(
              'w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white ring-1',
              isSubmitting || !legacyUsername || !legacyPassword
                ? 'bg-gray-300 ring-gray-300 cursor-not-allowed'
                : 'bg-primary ring-primary hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
            )}
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>

          <button
            type="button"
            onClick={() => {
              setError('');
              setLegacyUsername('');
              setLegacyPassword('');
              setAuthStep('login');
            }}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            Back to sign in
          </button>
        </form>
      </HeroShell>
    );
  }

  if (authStep === 'legacy-new-password') {
    return (
      <HeroShell
        icon={
          <img
            src={healthcomLogo}
            alt="HealthCom"
            className="size-14 rounded-2xl bg-white object-contain p-2"
          />
        }
        title="Set a new password"
        subtitle="This is your first sign-in. Choose a password to use from now on."
        footer={<>Trouble signing in? Contact your clinic.</>}
      >
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-500/10 p-3 ring-1 ring-red-100 dark:ring-red-500/20">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        <form onSubmit={handleLegacyNewPasswordSubmit} className="flex flex-col gap-4">
          <div>
            <label
              htmlFor="legacy-new-password"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              New password
            </label>
            <input
              id="legacy-new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              value={legacyNewPasswordValue}
              onChange={e => setLegacyNewPasswordValue(e.target.value)}
              className={cn(heroInputClass, 'mt-1.5')}
              required
              autoFocus
            />
          </div>
          <div>
            <label
              htmlFor="legacy-new-password-confirm"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Confirm new password
            </label>
            <input
              id="legacy-new-password-confirm"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              value={legacyNewPasswordConfirm}
              onChange={e => setLegacyNewPasswordConfirm(e.target.value)}
              className={cn(heroInputClass, 'mt-1.5')}
              required
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !legacyNewPasswordValue || !legacyNewPasswordConfirm}
            className={cn(
              'w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white ring-1',
              isSubmitting || !legacyNewPasswordValue || !legacyNewPasswordConfirm
                ? 'bg-gray-300 ring-gray-300 cursor-not-allowed'
                : 'bg-primary ring-primary hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
            )}
          >
            {isSubmitting ? 'Setting password…' : 'Set password and sign in'}
          </button>

          <button
            type="button"
            onClick={() => {
              setError('');
              setLegacyChallengeSession('');
              setLegacyNewPasswordValue('');
              setLegacyNewPasswordConfirm('');
              setAuthStep('legacy');
            }}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            Back to sign in
          </button>
        </form>
      </HeroShell>
    );
  }

  // ---------- Login step ----------
  return (
    <HeroShell
      icon={
        <img
          src={healthcomLogo}
          alt="HealthCom"
          className="size-14 rounded-2xl bg-white object-contain p-2"
        />
      }
      title="HealthCom Patient Portal"
      subtitle="Your health, in one place."
      footer={
        <div className="flex flex-col items-center gap-2">
          <span>Trouble signing in? Contact your clinic.</span>
          {/* Discreet legacy entry point. Patients with pre-OTP
              accounts still need a way in; this keeps the username/
              password path available without competing with the
              primary email/phone flow. */}
          <button
            type="button"
            onClick={() => {
              setError('');
              setAuthStep('legacy');
            }}
            className="text-[11px] text-white/60 hover:text-white/90 dark:text-zinc-500 dark:hover:text-zinc-300 underline-offset-2 hover:underline"
          >
            Use legacy username & password
          </button>
        </div>
      }
    >
      {/* Email / Phone toggle */}
      <div
        role="tablist"
        aria-label="Sign-in method"
        className="mb-5 grid grid-cols-2 rounded-xl bg-gray-100 dark:bg-zinc-900 p-1 text-sm font-medium"
      >
        {(['email', 'phone'] as const).map(m => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => {
              setMode(m);
              setError('');
            }}
            className={cn(
              'rounded-lg py-1.5 transition-colors',
              mode === m
                ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200 dark:bg-zinc-700 dark:text-zinc-100 dark:shadow-none dark:ring-white/10'
                : 'text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-200'
            )}
          >
            {m === 'email' ? 'Email' : 'Phone'}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-500/10 p-3 ring-1 ring-red-100 dark:ring-red-500/20">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <form onSubmit={handleLogin} className="flex flex-col gap-4">
        {mode === 'email' ? (
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={cn(heroInputClass, 'mt-1.5')}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>
        ) : (
          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Phone number
            </label>
            <div className="mt-1.5 flex gap-2">
              <label htmlFor="country" className="sr-only">
                Country
              </label>
              <select
                id="country"
                name="country"
                value={country}
                onChange={e => handleCountryChange(e.target.value as CountryCode)}
                className="shrink-0 rounded-xl border-0 bg-white dark:bg-zinc-900 pl-3 pr-7 py-2.5 text-sm text-gray-900 dark:text-zinc-100 ring-1 ring-gray-200 dark:ring-white/10 outline-2 -outline-offset-1 outline-transparent focus:outline-primary max-sm:text-base/5 appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 8 5%22 width=%228%22 height=%225%22 fill=%22none%22 stroke=%22%236b7280%22><path d=%22M.5.5 4 4 7.5.5%22/></svg>')] bg-no-repeat bg-[right_0.625rem_center] tabular-nums"
                aria-label="Country"
              >
                {COUNTRY_OPTIONS.map(opt => (
                  <option key={opt.iso} value={opt.iso}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                id="phone"
                name="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                value={nationalNumber}
                onChange={e => handleNationalNumberChange(e.target.value)}
                className={cn(heroInputClass, 'flex-1 min-w-0 tabular-nums')}
                placeholder={new AsYouType(country).input('5551234567')}
                required
                autoFocus
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className={cn(
            'w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white ring-1',
            !canSubmit
              ? 'bg-gray-300 ring-gray-300 cursor-not-allowed'
              : 'bg-primary ring-primary hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
          )}
        >
          {isSubmitting ? 'Sending…' : 'Continue'}
        </button>
      </form>
    </HeroShell>
  );
};

export default RootLanding;
